import React, { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Lightbulb,
  ExternalLink,
  FileText,
} from "lucide-react";

const SupportStrategiesUI = ({ response }: any) => {
  const [expandedStrategy, setExpandedStrategy] = useState<number | null>(null);

  const supportStrategies = response?.support_strategies || [];


   const getImpactColor = (confidence: number) => {
    if (confidence >= 0.85) return 'bg-green-100 text-green-800 border-green-300';
    if (confidence >= 0.7) return '  bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getImpactLabel = (confidence: number) => {
    if (confidence >= 0.85) return 'High Impact';
    if (confidence >= 0.7) return 'Medium Impact';
    return 'Low Impact';
  };

  const confidencebar = (confidence:number)=>{
    if (confidence >= 0.85) return 'bg-green-500 ';
    if (confidence >= 0.7) return '  bg-yellow-500 ';
    return 'bg-red-500 ';
  }

  

  return (
    <div className="h-[90vh] p-4 border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-3 h-3 text-blue-600" />
          <h1 className="text-md font-semibold text-slate-800">
            Support Strategy Recommendations
          </h1>
        </div>

        {/* Strategies */}
        {supportStrategies?.map((item: any, index: number) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden"
          >
            {/* Strategy Header */}
            <div
              className="p-2 flex justify-between items-start cursor-pointer hover:bg-slate-50 transition"
              onClick={() =>
                setExpandedStrategy(expandedStrategy === index ? null : index)
              }
            >
              <div className="flex flex-col gap-1">
                <div className="flex">
                    <h2 className="text-sm font-semibold text-slate-800 leading-snug">
                      {item.strategy.suggestion}
                    </h2>
                    <div>
                      {expandedStrategy === index ? (
                        <ChevronDown className="w-3 h-3 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-3 h-3 text-slate-500" />
                      )}
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-300">
                    {item.strategy.initiative_type}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getImpactColor(
                      item.strategy.impact_score
                    )}`}
                  >
                    {getImpactLabel(item.strategy.impact_score)}
                  </span>
                  <span className="text-[10px] text-slate-500 font-mono">
                    {(item.strategy.impact_score * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
              
            </div>

            {/* Impact Bar */}
            <div className="px-4 pb-3">
              <div className="w-full bg-slate-200 rounded-full h-1">
                <div
                  className={`h-1  ${confidencebar(
                      item.strategy.impact_score
                    )} rounded-full`}
                  style={{ width: `${item.strategy.impact_score * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Expanded Section */}
            {expandedStrategy === index && (
              <div className="bg-slate-50 border-t border-slate-200 p-4 space-y-4">
                {/* Description */}
                <div>
                  <p className="text-[11px] text-slate-500 font-medium uppercase mb-1">
                    Description
                  </p>
                  <div className="bg-white rounded-lg p-3 border-l-4 border-blue-400">
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {item.description || "No description available."}
                    </p>
                  </div>
                </div>

                {/* Evidence Sources */}
                <div>
                  <p className="text-[11px] text-slate-500 font-medium uppercase mb-2">
                    External Sources
                  </p>
                  <div className="space-y-2">
                    {item.evidence_sources.map((source: any, i: number) => (
                      <a
                        key={i}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 bg-white p-3 rounded-lg border border-slate-200 hover:bg-blue-50 transition"
                      >
                        {source.favicon_url ? (
                          <img
                            src={source.favicon_url}
                            alt="favicon"
                            className="w-4 h-4 mt-1 rounded-sm"
                          />
                        ) : (
                          <FileText className="w-4 h-4 text-slate-400 mt-1" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-blue-700 flex items-center gap-1">
                            {source.title}
                            <ExternalLink className="w-3 h-3" />
                          </p>
                          <p className="text-xs text-slate-600">
                            {source.summary}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                            source.reliability_score >= 0.8
                              ? "bg-green-100 text-green-800 border-green-300"
                              : "bg-orange-100 text-orange-800 border-orange-300"
                          }`}
                        >
                          {(source.reliability_score * 100).toFixed(0)}% Reliable
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Tip */}
        <div className="mt-6 bg-blue-50 rounded-lg p-3 border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Click on a strategy to explore its detailed
            description and supporting evidence sources.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportStrategiesUI;
